/**
 * Stats Section Component
 *
 * React implementation of the stats section renderer.
 * Supports variants: counter, grid, banner
 */

import { StatsData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface StatsProps extends SectionProps<StatsData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Stats({ data, ctx, sectionId, elements }: StatsProps) {
  const variant = data.variant || 'counter';

  switch (variant) {
    case 'grid':
      return <StatsGrid data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'banner':
      return <StatsBanner data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <StatsCounter data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// COUNTER VARIANT
// =============================================================================

function StatsCounter({ data, ctx, elements }: StatsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="stats"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {(data.headline || headlineEl) && (
          <div className="text-center mb-12">
            {(data.eyebrow || eyebrowEl) && (
              <div className="mb-4">
                {eyebrowEl ? (
                  <EditableText
                    element={eyebrowEl}
                    as="span"
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  />
                ) : (
                  <span
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  >
                    {data.eyebrow}
                  </span>
                )}
              </div>
            )}

            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl font-bold"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl font-bold"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              >
                {data.headline}
              </h2>
            ) : null}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {data.stats?.map((stat, i) => {
            const valueEl = findElement(elements, `stats.${i}.value`);
            const labelEl = findElement(elements, `stats.${i}.label`);

            return (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2" style={{ color: recipe.colors.primary }}>
                  {stat.prefix}
                  {valueEl ? (
                    <EditableText element={valueEl} as="span" />
                  ) : (
                    stat.value
                  )}
                  {stat.suffix}
                </div>
                {labelEl ? (
                  <EditableText
                    element={labelEl}
                    as="p"
                    className="text-lg"
                    style={{ color: recipe.colors.textMuted }}
                  />
                ) : (
                  <p className="text-lg" style={{ color: recipe.colors.textMuted }}>
                    {stat.label}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// GRID VARIANT
// =============================================================================

function StatsGrid({ data, ctx, elements }: StatsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="stats"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {(data.headline || headlineEl) && (
          <div className="text-center mb-12">
            {(data.eyebrow || eyebrowEl) && (
              <div className="mb-4">
                {eyebrowEl ? (
                  <EditableText
                    element={eyebrowEl}
                    as="span"
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  />
                ) : (
                  <span
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  >
                    {data.eyebrow}
                  </span>
                )}
              </div>
            )}

            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl font-bold"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl font-bold"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              >
                {data.headline}
              </h2>
            ) : null}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {data.stats?.map((stat, i) => {
            const valueEl = findElement(elements, `stats.${i}.value`);
            const labelEl = findElement(elements, `stats.${i}.label`);

            return (
              <div
                key={i}
                className="p-6 rounded-xl text-center"
                style={{
                  backgroundColor: recipe.colors.background,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {stat.icon && (
                  <div className="text-3xl mb-3">{stat.icon}</div>
                )}
                <div className="text-3xl md:text-4xl font-bold mb-2" style={{ color: recipe.colors.primary }}>
                  {stat.prefix}
                  {valueEl ? (
                    <EditableText element={valueEl} as="span" />
                  ) : (
                    stat.value
                  )}
                  {stat.suffix}
                </div>
                {labelEl ? (
                  <EditableText
                    element={labelEl}
                    as="p"
                    style={{ color: recipe.colors.textMuted }}
                  />
                ) : (
                  <p style={{ color: recipe.colors.textMuted }}>{stat.label}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// BANNER VARIANT
// =============================================================================

function StatsBanner({ data, ctx, elements }: StatsProps) {
  const { recipe } = ctx;

  return (
    <section
      id="stats"
      className="py-12"
      style={{
        backgroundColor: recipe.colors.primary,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {data.stats?.map((stat, i) => {
            const valueEl = findElement(elements, `stats.${i}.value`);
            const labelEl = findElement(elements, `stats.${i}.label`);

            return (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  {stat.prefix}
                  {valueEl ? (
                    <EditableText element={valueEl} as="span" />
                  ) : (
                    stat.value
                  )}
                  {stat.suffix}
                </div>
                {labelEl ? (
                  <EditableText
                    element={labelEl}
                    as="p"
                    className="text-white/80"
                  />
                ) : (
                  <p className="text-white/80">{stat.label}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Stats;
